import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const shapes = [
  { id: 'round', name: 'ROUND', rec: 'Angular frames like Square or Wayfarer add necessary structure and contrast to softer features.', img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=600' },
  { id: 'square', name: 'SQUARE', rec: 'Round or Oval frames soften strong jawlines and create a balanced, architectural silhouette.', img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=600' },
  { id: 'oval', name: 'OVAL', rec: 'The most versatile shape. Most frames work seamlessly, especially oversized and geometric styles.', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=600' },
  { id: 'heart', name: 'HEART', rec: 'Aviators or rimless frames balance wider foreheads and narrower chin lines for a refined look.', img: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&q=80&w=600' }
];

const FaceShapeGuide = () => {
  const [active, setActive] = useState(shapes[0]);

  return (
    <section id="guide" className="py-32 bg-black text-white overflow-hidden relative">
      <div className="absolute top-0 left-0 w-full h-1 prism-gradient opacity-50"></div>
      
      <div className="max-w-[1600px] mx-auto px-6">
        <div className="text-center mb-24">
          <h2 className="text-6xl md:text-9xl font-black tracking-tighter uppercase mb-4">
            Fit <span className="prism-text">Matrix</span>.
          </h2>
          <p className="text-gray-400 font-bold tracking-[0.3em] uppercase text-xs">Match your geometry to ours</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-center">
          {/* Controls */}
          <div className="lg:col-span-5 space-y-4 order-2 lg:order-1">
            {shapes.map((shape) => (
              <button
                key={shape.id}
                onClick={() => setActive(shape)}
                className={`w-full text-left p-8 border-2 transition-all duration-500 group flex justify-between items-center ${
                  active.id === shape.id 
                    ? 'border-white bg-white text-black' 
                    : 'border-white/10 text-gray-500 hover:border-white/40'
                }`}
              >
                <div className="flex items-center gap-6">
                  <span className={`text-[10px] font-black tracking-widest ${active.id === shape.id ? 'text-black/40' : 'text-gray-700'}`}>0{shapes.indexOf(shape) + 1}</span>
                  <span className="text-3xl font-black tracking-tight uppercase">{shape.name}</span>
                </div>
                <SafeIcon 
                  icon={FiIcons.FiArrowRight} 
                  className={`text-2xl transition-transform duration-500 ${active.id === shape.id ? 'translate-x-0' : '-translate-x-4 opacity-0'}`} 
                />
              </button>
            ))}
          </div>

          {/* Display */}
          <div className="lg:col-span-7 relative h-[500px] md:h-[650px] border-2 border-white/20 p-8 md:p-12 flex flex-col justify-end overflow-hidden order-1 lg:order-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 z-0"
              >
                <img src={active.img} alt={active.name} className="w-full h-full object-cover opacity-50 grayscale contrast-125" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
              </motion.div>
            </AnimatePresence>

            <motion.div 
              key={`${active.id}-content`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="relative z-10"
            >
              <h3 className="text-5xl md:text-7xl font-black mb-6 prism-text uppercase leading-none">{active.name}</h3>
              <p className="text-xl md:text-3xl font-medium max-w-xl leading-snug">
                {active.rec}
              </p>
              <div className="mt-8 flex gap-4">
                <button className="bg-white text-black px-8 py-4 font-black text-xs tracking-widest uppercase hover:prism-gradient transition-all">SHOP FOR {active.name}</button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaceShapeGuide;